import { useState, useEffect } from "react";
import { api, gr } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MagnifyingGlass } from "@phosphor-icons/react";

const STATUS = {
  new: { label: "Νέο", cls: "bg-[#DBEAFE] text-[#1E40AF]" },
  in_progress: { label: "Σε επεξεργασία", cls: "bg-[#FEF3C7] text-[#92400E]" },
  answered: { label: "Απαντήθηκε", cls: "bg-[#DCFCE7] text-[#065F46]" },
};

export default function TrackInquiryDialog({ open, onOpenChange }) {
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [data, setData] = useState(null);

  useEffect(() => { if (open) { setCode(""); setErr(""); setData(null); } }, [open]);

  const lookup = async () => {
    setErr(""); setData(null);
    if (!code.trim()) return setErr("Δώστε τον κωδικό.");
    setLoading(true);
    try {
      const res = (await api.get(`/public/inquiries/${encodeURIComponent(code.trim())}`)).data;
      setData(res);
    } catch (e) {
      setErr(e.response?.data?.detail || "Δεν βρέθηκε.");
    } finally { setLoading(false); }
  };

  const st = data ? (STATUS[data.status] || STATUS.new) : null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm sm:max-w-md" data-testid="track-inquiry-dialog">
        <DialogHeader>
          <DialogTitle className="font-head text-[#234E7D]">Παρακολούθηση αιτήματος</DialogTitle>
          <DialogDescription>Εισάγετε τον κωδικό που λάβατε κατά την αποστολή.</DialogDescription>
        </DialogHeader>
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <Label className="text-xs text-muted-foreground">Κωδικός</Label>
            <Input value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} className="mt-1 rounded-sm tracking-widest" data-testid="track-code-input" />
          </div>
          <Button onClick={lookup} disabled={loading} className="rounded-sm bg-[#0055FF] hover:bg-[#0044CC]" data-testid="track-submit"><MagnifyingGlass size={16} weight="bold" /></Button>
        </div>
        {err && <div className="mt-3 rounded-sm border border-destructive/30 bg-destructive/5 p-2 text-sm text-destructive" data-testid="track-error">{err}</div>}
        {data && (
          <div className="mt-3 space-y-2 rounded-sm border border-border p-3 text-sm" data-testid="track-result">
            <div className="flex items-center justify-between">
              <span className="font-semibold text-[#234E7D]">{data.marina_name}</span>
              <span className={`rounded-sm px-2 py-0.5 text-xs font-bold ${st.cls}`} data-testid="track-status">{st.label}</span>
            </div>
            {(data.length || data.start_date) && (
              <div className="text-xs text-muted-foreground">Σκάφος {data.length}μ · {gr(data.start_date)} → {gr(data.end_date)}</div>
            )}
            {data.message && <div className="text-muted-foreground">«{data.message}»</div>}
            <div className="rounded-sm bg-secondary/60 p-2">
              <div className="text-xs font-semibold text-muted-foreground">Απάντηση διαχειριστή</div>
              <div className="mt-0.5" data-testid="track-reply">{data.reply || "— (δεν υπάρχει απάντηση ακόμη)"}</div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
